import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFacebookF, FaInstagram } from 'react-icons/fa';

import styles from '../../assets/styles/Header.module.css';
import staticLogo from '../../assets/images/logo.png';
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API_BASE = `${ENV_API_BASE_URL}/api`;
const ASSET_BASE = `${ENV_API_BASE_URL}`;

const FALLBACK = {
  address:
    'Hatha Yogashram, Balaknath Rd, Upper Tapovan, Rishikesh, Uttarakhand India 249192',
  phone: '+91 9335606336',
  yogaAllianceId: 'Yoga Alliance ID: 401771',
  facebookUrl: 'https://www.facebook.com/profile.php?id=100095297992781',
  instagramUrl: 'https://www.instagram.com/hathayogashram/',
  logoUrl: '',
  ctaLabel: 'Register Now',
  ctaPath: '/BookingForm',
  navItems: [
    { type: 'link', label: 'Home', path: '/', side: 'left', order: 0 },
    {
      type: 'dropdown',
      label: 'About',
      side: 'left',
      order: 1,
      dropdown: {
        label: 'About',
        links: [
          { label: 'Founder', path: '/Our-Founder' },
          { label: 'Our Teachers', path: '/teachers' },
          { label: 'Our School', path: '/Our-School' },
          { label: 'Accommodation', path: '/Accommodation-Food' },
          { label: 'Blog', path: '/blog' },
          { label: 'Yoga Books', path: '/Yoga-Books' },
          { label: 'Gallery', path: '/gallery' },
        ],
      },
    },
    {
      type: 'dropdown',
      label: 'Rishikesh TTC',
      side: 'left',
      order: 2,
      dropdown: {
        label: 'Rishikesh TTC',
        links: [
          { label: '100 Hour TTC', path: '/course/100-hour-yttc' },
          { label: '200 Hour TTC', path: '/course/200-hour-yttc' },
          { label: '300 Hour TTC', path: '/course/300-hour-yttc' },
          { label: '500 Hour TTC', path: '/course/500-hour-yttc' },
        ],
      },
    },
    {
      type: 'dropdown',
      label: 'Short Course',
      side: 'left',
      order: 3,
      dropdown: {
        label: 'Short Course',
        links: [
          { label: 'Meditation', path: '/course/meditation' },
          { label: 'Pranayama', path: '/course/pranayama' },
        ],
      },
    },
    { type: 'link', label: 'Ayurveda', path: '/Ayurveda-Page', side: 'right', order: 4 },
    {
      type: 'dropdown',
      label: 'Online Courses',
      side: 'right',
      order: 5,
      dropdown: {
        label: 'Online Courses',
        links: [
          { label: 'Online YTTC', path: '/Online-YTTC' },
          { label: 'Yoga Online', path: '/Yoga-Online' },
        ],
      },
    },
    { type: 'link', label: 'Payment', path: '/payment', side: 'right', order: 6 },
    { type: 'link', label: 'Contact', path: '/contact-us', side: 'right', order: 7 },
  ],
};

const toAssetUrl = (value) => {
  if (!value) return '';
  if (value.startsWith('http') || value.startsWith('data:')) return value;
  return `${ASSET_BASE}/${value.replace(/^\/+/, '')}`;
};

const normalizeHeaderData = (data) => {
  if (!data) return FALLBACK;
  return {
    ...FALLBACK,
    ...data,
    navItems:
      Array.isArray(data.navItems) && data.navItems.length > 0
        ? data.navItems
        : FALLBACK.navItems,
  };
};

const Header = () => {
  const [headerData, setHeaderData] = useState(FALLBACK);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/header`)
      .then((res) => res.json())
      .then((json) => {
        if (json?.success) {
          setHeaderData(normalizeHeaderData(json.data));
        }
      })
      .catch(() => {});
  }, []);

  const closeAll = () => {
    setOpenDropdown(null);
    setMobileOpen(false);
    setMobileDropdown(null);
  };

  const toggleMobile = (name) => {
    setMobileDropdown(mobileDropdown === name ? null : name);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const sortedItems = [...(headerData.navItems || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const leftItems = sortedItems.filter((item) => item.side === 'left');
  const rightItems = sortedItems.filter((item) => item.side === 'right');
  const logoSrc = headerData.logoUrl ? toAssetUrl(headerData.logoUrl) : staticLogo;

  const DesktopDropdown = ({ name, label, links }) => (
    <div
      className={styles.dropdownWrap}
      onMouseEnter={() => setOpenDropdown(name)}
      onMouseLeave={() => setOpenDropdown(null)}
    >
      <button type="button" className={styles.dropdownBtn}>
        {label} ▾
      </button>

      {openDropdown === name && <div className={styles.bridge} />}

      <AnimatePresence>
        {openDropdown === name && (
          <motion.div
            className={styles.dropdown}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
          >
            {links.map((link, index) => (
              <Link key={`${link.path}-${index}`} to={link.path} onClick={closeAll}>
                {link.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const MobileAccordion = ({ name, label, links }) => (
    <>
      <div className={styles.mobileTitle} onClick={() => toggleMobile(name)}>
        <span>{label}</span>
        <span className={styles.arrow}>{mobileDropdown === name ? '▴' : '▾'}</span>
      </div>
      {mobileDropdown === name && (
        <div className={styles.subMenu}>
          {links.map((link, index) => (
            <Link key={`${link.path}-${index}`} to={link.path} onClick={closeAll}>
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </>
  );

  const renderDesktopItem = (item, index) => {
    if (item.type === 'dropdown' && item.dropdown?.links?.length) {
      return (
        <DesktopDropdown
          key={`${item.label}-${index}`}
          name={`${item.side}-${index}`}
          label={item.label}
          links={item.dropdown.links}
        />
      );
    }

    return (
      <Link key={`${item.label}-${index}`} to={item.path || '/'} onClick={closeAll}>
        {item.label}
      </Link>
    );
  };

  const renderMobileItem = (item, index) => {
    if (item.type === 'dropdown' && item.dropdown?.links?.length) {
      return (
        <MobileAccordion
          key={`${item.label}-${index}`}
          name={`${item.side}-${index}`}
          label={item.label}
          links={item.dropdown.links}
        />
      );
    }

    return (
      <Link
        key={`${item.label}-${index}`}
        to={item.path || '/'}
        onClick={closeAll}
        className={styles.mobileLink}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <>
      <div className={styles.topHeader}>
        <div className={styles.topContainer}>
          <div className={styles.topLeft}>
            {headerData.address && <span>📍 {headerData.address}</span>}
            {headerData.phone && <span>📞 {headerData.phone}</span>}
          </div>
          <div className={styles.topRight}>
            {headerData.yogaAllianceId && (
              <span className={styles.yogaId}>{headerData.yogaAllianceId}</span>
            )}
            <div className={styles.social}>
              {headerData.facebookUrl && (
                <a
                  href={headerData.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaFacebookF />
                </a>
              )}
              {headerData.instagramUrl && (
                <a
                  href={headerData.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaInstagram />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <header className={styles.header}>
        <div className={styles.container} ref={navRef}>
          <nav className={styles.desktopNav}>{leftItems.map(renderDesktopItem)}</nav>

          <div className={styles.logo}>
            <Link to="/">
              <img src={logoSrc} alt="Yoga Logo" />
            </Link>
          </div>

          <nav className={styles.desktopNav}>
            {rightItems.map(renderDesktopItem)}
            <Link to={headerData.ctaPath || '/BookingForm'} className={styles.applyBtn} onClick={closeAll}>
              {headerData.ctaLabel || 'Register Now'}
            </Link>
          </nav>

          <div className={styles.hamburger} onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? '✕' : '☰'}
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              className={styles.mobileMenu}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              {leftItems.map(renderMobileItem)}
              {rightItems.map(renderMobileItem)}
              <Link
                to={headerData.ctaPath || '/BookingForm'}
                className={styles.applyBtn}
                onClick={closeAll}
              >
                {headerData.ctaLabel || 'Register Now'}
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
